import SessionManager from "@/apollo/datasources/sessionManager";
import config from "@/lib/config";
import connectRedis from "@/lib/connectRedis";
import Cookies from "@/lib/cookies";
import dayjs from "@/lib/day";
import { AuthenticationError } from "apollo-server-errors";
import { createHash } from "crypto";
import jwt from "jsonwebtoken";

export default class IdcToken {
  constructor(req, res) {
    this.req = req;
    this.res = res;
    this.cookies = new Cookies(req, res);
    this.redis = connectRedis(
      {
        host: process.env.REDIS_HOST || config.idcToken.redis?.host,
        port: process.env.REDIS_PORT || config.idcToken.redis?.port,
        keyPrefix: config.idcToken.redis.prefix,
        db: config.idcToken.redis?.db,
      },
      config.idcToken?.redis?.cluster
    );
  }

  get cookieName() {
    return config.idcToken.name;
  }

  getToken() {
    return this.cookies.get(this.cookieName);
  }

  get signatureName() {
    return config.idcToken.signature;
  }

  getSignature() {
    return this.cookies.get(this.signatureName);
  }

  getSecret(signature = null) {
    if (!signature) {
      signature = this.getSignature();
    }
    return `${signature}${config.idcToken.secret}`;
  }

  generateKey(id, signature) {
    const key = (() => {
      return `idcToken:${id}:${signature}`;
    })();
    return createHash("sha256").update(key).digest("hex");
  }

  async setPayload(payload, signature) {
    const key = this.generateKey(payload.id, signature);
    await this.redis
      .multi()
      .set(key, JSON.stringify(payload))
      .expire(key, config.idcToken.maxAge)
      .exec();
    return key;
  }

  async getPayload(key) {
    const json = await this.redis.get(key);
    return JSON.parse(json);
  }

  deletePayload(key) {
    return this.redis.del(key);
  }

  async sign(payload, signature) {
    if (!signature) {
      signature = this.getSignature();
    }
    const key = await this.setPayload(payload, signature);
    const secret = this.getSecret(signature);
    const token = jwt.sign({ key }, secret);
    const options = { maxAge: config.idcToken.maxAge };
    this.cookies.set([
      { name: this.signatureName, value: signature, options },
      { name: this.cookieName, value: token, options },
    ]);
    return token;
  }

  async verify() {
    // cookieからtokenを取得
    const token = this.getToken();
    if (!token) return false;
    // cookieからsignatureを取得
    const secret = this.getSecret();
    // tokenを検証
    const verifiedToken = jwt.verify(token, secret);
    // redisからpayloadを取得
    const payload = await this.getPayload(verifiedToken.key);
    if (!payload) return false;
    return { ...verifiedToken, payload };
  }

  async delete() {
    const token = this.getToken();
    if (!token) {
      console.log("userToken delete, token not found");
      return false;
    }
    try {
      const secret = this.getSecret();
      const verifiedToken = jwt.verify(token, secret);
      await this.deletePayload(verifiedToken.key);
    } catch (e) {
      console.log("token delete error", e);
    } finally {
      this.cookies.remove([
        this.cookieName,
        this.signatureName,
        [config.optionalTermsConfirmCookieName, { domain: null }],
      ]);
    }
  }

  async deleteAll() {
    const removeCookies = [
      [config.optionalTermsConfirmCookieName, { domain: null }],
    ];
    try {
      [true, false].forEach(async (val) => {
        const token = this.getToken();
        if (token) {
          const secret = this.getSecret();
          const verifiedToken = jwt.verify(token, secret);
          removeCookies.push(this.cookieName);
          removeCookies.push(this.signatureName);
          await this.deletePayload(verifiedToken.key);
        }
      });
    } catch (e) {
      console.log("token delete error", e);
    } finally {
      this.cookies.remove(removeCookies);
    }
  }
}
